import _, { ObjectChain } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import ButtonGroup from '../../components/ButtonGroup';
import ChartWrapper, { chartWrapperPropTypesTypescript } from '../../components/ChartWrapper';
import { useFootnotes } from '../../components/Footnotes';
import Legend from '../../components/Legend';
import { useSources } from '../../components/Sources';
import {
  extractDatasets,
  extractKeys,
  getAxisTypeFromData,
  getDataIds,
  isValidAxisType,
  useDropdown,
  valueFormat
} from '../../utilities';
import { DEFAULT_COLORS } from '../../utilities/colors';
import { dateParser } from '../../utilities/date';
import { VALUE_FORMATS } from '../../utilities/format';
import { textDimensions } from '../../utilities/measurements';
import { dataShapeTypescript, sourcesShapeTypescript } from '../../utilities/types';
import ChartDescription from './ChartDescription';
import { DEFAULT_DROPDOWN_OPTIONS, DEFAULT_DROPDOWN_TYPE } from './constants';
import { getValueByDate } from './model';
import Mosaic from './Mosaic';
import ParsedTemplate from './ParsedTemplate';
import './styles.css';
import {
  validateCategories,
  validateGroups,
  validateMetrics,
  validateTemplateString
} from './validate';

const MOSAIC_FONT_SIZE = 14;
const MIN_HEIGHT = 300;

type MosaicPlotProps = chartWrapperPropTypesTypescript & {
  /**
   * Height to draw the chart. Will auto calculate if unset.
   */
  height?: number;
  /**
   * Width to draw the chart. Will fill container if unset.
   */
  width?: number;
  /**
   * IDs and labels of groups
   */
  groups: {
    id: string;
    label: string;
  }[];
  /**
   * IDs, labels and colors of categories
   */
  categories?: {
    id: string;
    label: string;
    color?: string;
  }[];
  /**
   * Defines which group and category each data belongs to. Allows override of name and color.
   */
  metrics: {
    group: string;
    category: string;
    metrics: {
      name: string;
      id: string;
      color?: string;
    }[];
  }[];
  /**
   * Data for the chart
   */
  data:
    | dataShapeTypescript[]
    | {
        datasets?: dataShapeTypescript[];
        sources?: sourcesShapeTypescript[];
      }[];
  /**
   * Configuration options for dropdown
   * @param enabled {bool} set ability to change selected item
   * @param selectedItem {string} default selected item for dropdown,
   * must be an exact match in value & format to a key in data array
   * @param show {bool} show or hide dropdowns
   * @param type {string} data type - 'items','linear','unformatted','percent','dollars','years',
   * 'yearly','months','monthly','quarters','quarterly','weeks','weekly','days','daily'
   */
  dropdownOptions: {
    enabled?: boolean;
    selectedItem?: string | number;
    show?: boolean;
    sort?: boolean | 'ascending' | 'descending';
    type?: string;
  };
  /**
   * The categories that are selected by default
   */
  defaultSelected?: string[];
  /**
   * The markup string template for the default chart description
   *
   * Variables are delimited by surround them in curly braces. ex: {variable}
   * Text transformation can be applied to the variable after a dot. ex: {variable.capitalize}
   * Variables can be bolded by surrounding the curly braces with underscores. ex: _{variable}_
   * Variables can be bolded and colored by surrounding the curly braces asterisks. ex: *{variable}*
   *
   * Supported Variables:
   * - rowLabel     (string) - Ex: 'white'
   * - label        (string) - Ex: 'murder'
   * - value        (number) - Ex: 10000
   * - yType        (string) - Ex: 'prisoners'
   * - rowPercent   (string) - Ex: '10%'
   * - totalPercent (string) - Ex: '1%'
   *
   * Supported text transforms: capitalize, uppercase, lowercase
   */
  descriptionTemplate: string;
  /**
   * The markup string template for the focused/hovered chart description
   *
   * Variables are delimited by surround them in curly braces. ex: {variable}
   * Text transformation can be applied to the variable after a dot. ex: {variable.capitalize}
   * Variables can be bolded by surrounding the curly braces with underscores. ex: _{variable}_
   * Variables can be bolded and colored by surrounding the curly braces asterisks. ex: *{variable}*
   *
   * Supported Variables:
   * - rowLabel     (string) - Ex: 'white'
   * - label        (string) - Ex: 'murder'
   * - value        (number) - Ex: 10000
   * - yType        (string) - Ex: 'prisoners'
   * - rowPercent   (string) - Ex: '10%'
   * - totalPercent (string) - Ex: '1%'
   *
   * Supported text transforms: capitalize, uppercase, lowercase
   */
  selectedDescriptionTemplate: string;
  /**
   * The max number of lines that the description should wrap to (controls the height of the chart
   * description container)
   */
  descriptionLines: number;
  /**
   * The quantity type that the mosaic chart is proportioning (e.g. 'people')
   */
  yType: string;
  /**
   * The markup string template for the group label (e.g. 'of People')
   *
   * Variables are delimited by surround them in curly braces. ex: {variable}
   * Text transformation can be applied to the variable after a dot. ex: {variable.capitalize}
   * Variables can be bolded by surrounding the curly braces with underscores. ex: _{variable}_
   * Variables can be bolded and colored by surrounding the curly braces asterisks. ex: *{variable}*
   *
   * Supported Variables:
   * - yType        (string) - Ex: 'people'
   *
   * Supported text transforms: capitalize, uppercase, lowercase
   */
  groupLabelTemplate: string;
  /**
   * Flag to hide sources footer. Defaults to false.
   */
  hideFooter: boolean;
  /**
   * Allows manual override of label section width
   */
  labelWidth: number;
};

const MosaicPlot = ({
  title,
  subtitle,
  titleFontSize,
  subtitleFontSize,
  height,
  width,
  data: rawData,
  dropdownOptions = DEFAULT_DROPDOWN_OPTIONS,
  groups,
  categories,
  metrics,
  defaultSelected = [],
  descriptionTemplate,
  selectedDescriptionTemplate,
  descriptionLines,
  yType,
  groupLabelTemplate = '',
  hideFooter = false,
  labelWidth = 0
}: MosaicPlotProps) => {
  if (height && height < MIN_HEIGHT) {
    console.warn(`Height cannot be lower than ${MIN_HEIGHT}.`);
  }

  const descriptionTemplateVariables = [
    'yType',
    'label',
    'value',
    'rowLabel',
    'rowPercent',
    'totalPercent'
  ];
  const divRef = useRef();
  const optsDropdown = { ...DEFAULT_DROPDOWN_OPTIONS, ...dropdownOptions };
  const dataIdsString = getDataIds(rawData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => extractDatasets(rawData), [dataIdsString]);

  const dropdownType = isValidAxisType(optsDropdown.type)
    ? optsDropdown.type
    : getAxisTypeFromData(data, 'x') || DEFAULT_DROPDOWN_TYPE;
  const dropdownFormat = useMemo(
    () => valueFormat({ params: { format: dropdownType } }),
    [dropdownType]
  );

  const keys = useMemo(
    () =>
      extractKeys(data, dateParser).map((i) => ({
        ...i,
        parsed: dropdownFormat(i.key)
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataIdsString, dropdownType]
  );

  const { key: date, dropdown } = useDropdown({
    enabled: optsDropdown.enabled,
    keys,
    label: title ? ' ' : '',
    parser: dropdownFormat,
    sortBy: optsDropdown.sort,
    targetKey: optsDropdown.selectedItem
  });
  const [selectedCategories, setSelectedCategories] = useState(defaultSelected);

  const [selected, setSelected] = useState(null);

  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  const totalValue = data.map(getValueByDate(date)).reduce((sum, value) => sum + value, 0);

  const totalByMetric = _(data).keyBy('id').mapValues(getValueByDate(date)).value();

  const metricsByGroupByCategory = _(metrics)
    .groupBy('group')
    .mapValues((mappedGroups) =>
      _(mappedGroups)
        .keyBy('category')
        .mapValues(({ metrics: newMetrics }) => newMetrics)
        .value()
    )
    .value();

  const totalsByGroup = _(metrics)
    .groupBy('group')
    .mapValues((mappedGroups) => _(mappedGroups).flatMap('metrics').map('id').value())
    .mapValues((ids) => _.sumBy(ids, (id) => totalByMetric[id]))
    .value();

  const totalByGroupByCategory = _(metrics)
    .groupBy('group')
    .mapValues((mappedGroups) =>
      _(mappedGroups)
        .keyBy('category')
        .mapValues(({ metrics: newMetrics }) => _.sumBy(newMetrics, ({ id }) => totalByMetric[id]))
        .value()
    )
    .value();

  const proportionsBySubgroup = _(metrics)
    .groupBy('group')
    .mapValues((mappedGroups) =>
      _(mappedGroups)
        .keyBy('category')
        .mapValues(({ metrics: newMetrics }) =>
          _(newMetrics)
            .keyBy('id')
            .mapValues(({ id }) => totalByMetric[id] / totalValue)
            .value()
        )
        .value()
    )
    .value();

  const subcategoriesByCategory = _(metrics)
    .groupBy('category')
    .mapValues((mappedGroups) => _(mappedGroups).flatMap('metrics').map('name').uniq().value())
    .value();

  const categoryToColor = {};
  categories.forEach(({ id, color }, i) => {
    categoryToColor[id] = color || DEFAULT_COLORS[i];
  });

  const { category, group, metric } = selected || {};
  const emphasisColor = category ? categoryToColor[category] : null;
  const descriptionRowLabel = group ? group.toLowerCase() : null;

  let descriptionValue = totalValue;
  let descriptionLabel = null;
  let descriptionRowPercent = null;
  let descriptionTotalPercent = null;
  const descriptionFormat = VALUE_FORMATS.percent;
  if (selected) {
    if (metric) {
      const { name = '' } =
        ((_.chain(metrics).find({ group, category }) as ObjectChain<any>).get('metrics') as any)
          .find({ id: metric })
          .value() || {};
      descriptionLabel = name.toLowerCase();
      descriptionValue = totalByMetric[metric];
      descriptionRowPercent = descriptionFormat(totalByMetric[metric] / totalsByGroup[group]);
      descriptionTotalPercent = descriptionFormat(totalByMetric[metric] / totalValue);
    } else {
      const { label = '' } = _.find(categories, { id: category }) || {};
      descriptionLabel = label.toLowerCase();
      descriptionValue = totalByGroupByCategory[group][category];
      descriptionRowPercent = descriptionFormat(
        totalByGroupByCategory[group][category] / totalsByGroup[group]
      );
      descriptionTotalPercent = descriptionFormat(
        totalByGroupByCategory[group][category] / totalValue
      );
    }
  }

  const buttonCategories = categories
    .filter(({ id }) => subcategoriesByCategory[id].length > 1)
    .map(({ id, label }) => ({
      id,
      label,
      color: categoryToColor[id],
      selected: selectedCategories.includes(id)
    }));

  let footerSources = [];
  let footerSummary;
  let footnotes = [];

  const sources = useSources({ data });
  const footerFootnotes = useFootnotes({ data });

  if (!hideFooter) {
    footerSources = sources.sources;
    footerSummary = sources.summary;
    footnotes = Array.isArray(footerFootnotes) ? footerFootnotes : footerFootnotes?.footnotes;
  }

  if (
    !_.isEmpty(validateGroups(groups)) ||
    !_.isEmpty(validateCategories(categories)) ||
    !_.isEmpty(validateMetrics(groups, categories, metrics)) ||
    !_.isEmpty(validateTemplateString(descriptionTemplate, descriptionTemplateVariables)) ||
    !_.isEmpty(validateTemplateString(selectedDescriptionTemplate, descriptionTemplateVariables)) ||
    !_.isEmpty(validateTemplateString(groupLabelTemplate, ['yType']))
  ) {
    return null;
  }

  return (
    <ChartWrapper
      className="mosaic-plot"
      title={title}
      subtitle={subtitle}
      titleFontSize={titleFontSize}
      subtitleFontSize={subtitleFontSize}
      footerSources={footerSources}
      footerSummary={footerSummary}
      footnotes={footnotes}
      showFooter={!hideFooter}
      dropdowns={optsDropdown.show && dropdown ? [dropdown] : []}
      width={width || null}
      height={height || '100%'}
      fontSize={MOSAIC_FONT_SIZE}
    >
      {({ chartWidth, chartHeight }) => {
        const fixedChartHeight = chartHeight;
        const legendWidth = _.sum(
          categories.map(
            ({ label }) =>
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              MOSAIC_FONT_SIZE +
              4 +
              (textDimensions({ text: label, fontSize: MOSAIC_FONT_SIZE })?.width || 0) +
              20
          )
        );
        let legendHeight = categories.length
          ? MOSAIC_FONT_SIZE * 2.5 * Math.ceil(legendWidth / chartWidth) + 12
          : 0;
        if (legendHeight === Infinity) {
          legendHeight = 0;
        }
        let descriptionHeight = MOSAIC_FONT_SIZE * descriptionLines;
        if (descriptionHeight === Infinity) {
          descriptionHeight = 0;
        }
        let buttonHeight = buttonCategories.length ? 100 + 22 : 0;
        if (buttonHeight === Infinity) {
          buttonHeight = 0;
        }
        const minChartHeight = legendHeight + descriptionHeight + buttonHeight;
        let mosaicHeight =
          fixedChartHeight - minChartHeight <= 0 ? 1 : fixedChartHeight - minChartHeight;
        if (mosaicHeight < MIN_HEIGHT) {
          mosaicHeight = MIN_HEIGHT;
        }

        return (
          <div
            style={{
              height: chartHeight,
              minHeight: minChartHeight + mosaicHeight,
              overflow: 'visible',
              position: 'relative',
              width: '100%'
            }}
          >
            <Legend
              {...{
                categories,
                fontSize: MOSAIC_FONT_SIZE,
                style: { marginBottom: 12 }
              }}
            />
            <ChartDescription
              hovered={selected}
              descriptionTemplate={descriptionTemplate}
              selectedDescriptionTemplate={selectedDescriptionTemplate}
              descriptionLines={descriptionLines}
              value={VALUE_FORMATS.default(descriptionValue, { truncate: false })}
              label={descriptionLabel}
              emphasisColor={emphasisColor}
              rowLabel={descriptionRowLabel}
              rowPercent={descriptionRowPercent}
              totalPercent={descriptionTotalPercent}
              yType={yType}
              fontSize={MOSAIC_FONT_SIZE}
            />
            <div ref={divRef} style={{ height: mosaicHeight }}>
              <Mosaic
                {...{
                  groups,
                  categories,
                  metricsByGroupByCategory,
                  selectedCategories,
                  proportionsBySubgroup,
                  setSelected
                }}
                height={mosaicHeight}
                width={chartWidth || 1}
                targetRef={divRef}
                unitSpan={<ParsedTemplate template={groupLabelTemplate} yType={yType} />}
                fontSize={MOSAIC_FONT_SIZE}
                labelWidth={labelWidth}
              />
            </div>
            <ButtonGroup
              categories={buttonCategories}
              onButtonClick={(newCategory) => {
                if (selectedCategories.includes(newCategory)) {
                  setSelectedCategories(_.without(selectedCategories, newCategory));
                } else {
                  setSelectedCategories(selectedCategories.concat([newCategory]));
                }
              }}
            />
          </div>
        );
      }}
    </ChartWrapper>
  );
};

MosaicPlot.displayName = 'MosaicPlot';
export default MosaicPlot;
